// ─── Tag View Page ────────────────────────────────────────────────────────────
// Public product card opened by scanning a jewellery tag QR
// (https://<domain>/t/<sku>). No login required — fetches the tag data from
// the anonymous /api/tag/{sku} endpoint. Premium dark/gold storefront look.
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Tag, Scale, ShieldCheck, Package, Gem, IndianRupee, Tags,
  Truck, RotateCcw, Lock, Award, Share2, BadgeCheck,
  Headphones, MessageCircle, ShoppingBag,
} from 'lucide-react';
import { tagAPI } from '../services/api';

// Shop contact for the Support / WhatsApp / Buy Now buttons.
// Set the WhatsApp number in international format without '+' (e.g. '919574001210').
// Leave empty to hide the buttons.
const SHOP_PHONE = '+91 63560 39856';
const SHOP_WHATSAPP = '916356039856';

const GOLD = '#c8a24e';
const GOLD_DARK = '#b08a3e';
const NAVY = '#0b1220';

// Official WhatsApp glyph (green bubble + white handset)
const WhatsAppIcon = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
    <path fill="#25D366" d="M16 0a16 16 0 0 0-13.8 24l-2.2 8 8.2-2.1A16 16 0 1 0 16 0z" />
    <path fill="#fff" d="M12.1 8.6c-.3-.6-.5-.6-.8-.6h-.7c-.2 0-.6.1-.9.4-.3.3-1.2 1.2-1.2 2.8 0 1.7 1.2 3.3 1.4 3.5.2.2 2.4 3.8 5.9 5.2 2.9 1.1 3.5.9 4.1.9.6-.1 2-.8 2.3-1.6.3-.8.3-1.5.2-1.6-.1-.2-.3-.2-.7-.4-.3-.2-2-1-2.3-1.1-.3-.1-.5-.2-.8.2-.2.3-.9 1.1-1.1 1.3-.2.2-.4.2-.7.1-.4-.2-1.5-.6-2.9-1.8-1.1-.9-1.8-2.1-2-2.5-.2-.3 0-.5.1-.7.2-.2.3-.4.5-.6.2-.2.2-.3.3-.6.1-.2.1-.4 0-.6-.1-.2-.8-1.9-1.1-2.6z" />
  </svg>
);

export default function TagViewPage() {
  const { sku } = useParams();
  const [item, setItem] = useState(null);
  const [state, setState] = useState('loading'); // loading | ok | notfound | error

  useEffect(() => {
    let alive = true;
    tagAPI.get(sku)
      .then(r => { if (alive) { setItem(r.data?.data || null); setState(r.data?.data ? 'ok' : 'notfound'); } })
      .catch(err => { if (alive) setState(err.response?.status === 404 ? 'notfound' : 'error'); });
    return () => { alive = false; };
  }, [sku]);

  const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const price = item ? (item.salePrice > 0 ? item.salePrice : item.mrp) : 0;
  const hasDiscount = item && item.mrp > 0 && item.mrp > price;
  const discountPct = hasDiscount ? Math.round(((item.mrp - price) / item.mrp) * 100) : 0;
  const savings = hasDiscount ? item.mrp - price : 0;

  const formatTitle = (it) => {
    if (!it) return '';
    if (it.description && it.description.trim()) {
      const desc = it.description.trim();
      const name = (it.itemName || '').trim();
      const baseName = name.replace(/\s*\(.*\)/, '').replace(/s$/i, '').toLowerCase();
      if (baseName && desc.toLowerCase().includes(baseName)) {
        return desc;
      }
      return `${desc} ${name}`.trim();
    }
    return `${it.purityCode ? it.purityCode + ' ' : ''}Sterling Silver ${it.itemName || ''}`.trim();
  };

  const share = () => {
    const url = window.location.href;
    if (navigator.share) navigator.share({ title: `Carat Quest — ${item?.itemName || sku}`, url }).catch(() => {});
    else if (navigator.clipboard) navigator.clipboard.writeText(url).catch(() => {});
  };

  const waLink = (msg) => `https://wa.me/${SHOP_WHATSAPP}?text=${encodeURIComponent(msg)}`;

  // ── Small building blocks ──────────────────────────────────
  const DetailCell = ({ icon: Icon, label, value }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px' }}>
      <Icon size={16} color={GOLD_DARK} strokeWidth={1.7} style={{ flexShrink: 0 }} />
      <div style={{ textAlign: 'left', minWidth: 0 }}>
        <div style={{ fontSize: 10.5, color: '#64748b', lineHeight: 1.1 }}>{label}</div>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: '#111827', overflowWrap: 'anywhere', lineHeight: 1.25 }}>{value}</div>
      </div>
    </div>
  );

  const Feature = ({ icon: Icon, line1, line2 }) => (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, padding: '8px 2px' }}>
      <Icon size={16} color={GOLD} strokeWidth={1.7} />
      <div style={{ fontSize: 9.5, color: '#e5e7eb', textAlign: 'center', lineHeight: 1.2 }}>{line1}<br />{line2}</div>
    </div>
  );

  const cardMsg = state === 'notfound'
    ? { title: 'Not found', sub: <>No item with SKU <b>{sku}</b></> }
    : state === 'error'
      ? { title: 'Something went wrong', sub: 'Please try scanning again.' }
      : null;

  return (
    <div style={{ margin: 0, fontFamily: "'Segoe UI', Arial, sans-serif", background: NAVY, height: '100vh', maxHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px 10px 12px', boxSizing: 'border-box', overflow: 'hidden' }}>

      {/* ── Brand header — logo image ── */}
      <img src="/cq-logo-white.png" alt="Carat Quest" style={{ height: 26, width: 'auto', display: 'block', margin: '0 0 8px', flexShrink: 0 }} />

      <div style={{ width: '100%', maxWidth: 420, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', minHeight: 0 }}>

        {state === 'loading' && (
          <div style={{ background: '#fff', borderRadius: 16, padding: '30px 20px', textAlign: 'center', color: '#64748b' }}>Loading…</div>
        )}

        {cardMsg && (
          <div style={{ background: '#fff', borderRadius: 16, padding: '24px 20px', textAlign: 'center' }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a' }}>{cardMsg.title}</div>
            <div style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>{cardMsg.sub}</div>
          </div>
        )}

        {state === 'ok' && item && (
          <>
            {/* ── Photo card ── */}
            {/* Shorter box (~40% less tall than square) but object-fit:contain keeps the whole image visible, uncropped */}
            <div style={{ position: 'relative', borderRadius: '14px 14px 0 0', overflow: 'hidden', background: '#fff', aspectRatio: '5 / 3', flexShrink: 0 }}>
              {item.imageData
                ? <img src={item.imageData} alt={item.itemName} style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', background: '#fff' }} />
                : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 52, background: '#f1f5f9' }}>💍</div>}
              {hasDiscount && (
                <div style={{ position: 'absolute', top: 10, left: 10, background: GOLD, color: NAVY, fontWeight: 800, fontSize: 12, padding: '4px 11px', borderRadius: 16 }}>
                  {discountPct}% OFF
                </div>
              )}
              <button type="button" onClick={share} aria-label="Share"
                style={{ position: 'absolute', top: 10, right: 10, width: 34, height: 34, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,.22)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                <Share2 size={16} color="#fff" />
              </button>
            </div>

            {/* ── Name: item description / name ── */}
            <div style={{ background: '#101a2e', padding: '10px 14px', flexShrink: 0 }}>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#fff', lineHeight: 1.2 }}>
                {formatTitle(item)}
              </div>
              {item.collection && (
                <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 2 }}>
                  Collection :- {item.collection}
                </div>
              )}
            </div>

            {/* ── Price + details ── */}
            <div style={{ background: '#fff', padding: '10px 12px', borderRadius: '0 0 14px 14px', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                  <div style={{ fontSize: 24, fontWeight: 800, color: GOLD_DARK }}>₹{fmt(price)}</div>
                  {hasDiscount && (
                    <div style={{ border: `1.5px solid ${GOLD}`, color: GOLD_DARK, fontWeight: 700, fontSize: 11, padding: '2px 8px', borderRadius: 6 }}>
                      {discountPct}% OFF
                    </div>
                  )}
                </div>
                {hasDiscount && (
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ color: '#94a3b8', fontSize: 13, textDecoration: 'line-through', marginRight: 6 }}>₹{fmt(item.mrp)}</span>
                    <span style={{ color: '#16a34a', fontWeight: 700, fontSize: 12 }}>Save ₹{fmt(savings)}</span>
                  </div>
                )}
              </div>

              {/* Details grid */}
              <div style={{ border: '1px solid #eef1f5', borderRadius: 10, marginTop: 8, display: 'grid', gridTemplateColumns: '1fr 1fr', background: '#fbfcfe', padding: '4px 2px' }}>
                <DetailCell icon={Tag}         label="SKU No"     value={item.skuNo} />
                <DetailCell icon={Scale}       label="Weight"     value={<>{Number(item.weight || 0).toFixed(3)} g <span style={{ fontWeight: 500, fontSize: 10, color: '#64748b' }}>(per pc)</span></>} />
                <DetailCell icon={ShieldCheck} label="Purity"     value={`${item.purityCode} Silver`} />
                <DetailCell icon={Package}     label="Quantity"   value={Number(item.quantity || 0)} />
                <DetailCell icon={Gem}         label="Category"   value={item.itemName} />
                <DetailCell icon={BadgeCheck}  label="Status"     value={item.productStatus || '—'} />
                <DetailCell icon={IndianRupee} label="MRP"        value={`₹${fmt(item.mrp)}`} />
                <DetailCell icon={Tags}        label="Sale Price" value={`₹${fmt(price)}`} />
              </div>
            </div>

            {/* ── Certified strip ── */}
            <div style={{ background: '#f8f4ea', border: `1px solid ${GOLD}55`, borderRadius: 12, marginTop: 6, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
              <div style={{ width: 42, height: 42, borderRadius: '50%', border: `1.5px solid ${GOLD_DARK}`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: GOLD_DARK, flexShrink: 0 }}>
                <Award size={18} strokeWidth={1.8} />
                <div style={{ fontSize: 6, fontWeight: 800, letterSpacing: '.03em' }}>925 SILVER</div>
              </div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontWeight: 800, color: '#111827', fontSize: 13 }}>Guaranteed Authentic</div>
                <div style={{ color: '#6b7280', fontSize: 11, marginTop: 1 }}>Each product is 100% certified {item.purityCode} Sterling Silver.</div>
              </div>
            </div>

            {/* ── Feature strip ── */}
            <div style={{ background: '#101a2e', borderRadius: 12, marginTop: 6, display: 'flex', flexShrink: 0 }}>
              <Feature icon={Truck}     line1="Free Shipping"  line2="PAN India" />
              <Feature icon={RotateCcw} line1="Easy Returns"   line2="7 Days" />
              <Feature icon={Lock}      line1="Secure Payment" line2="100% Safe" />
              <Feature icon={Award}     line1="Premium Quality" line2="Assured" />
            </div>

            {/* ── Action buttons ── */}
            {(SHOP_PHONE || SHOP_WHATSAPP) && (
              <div style={{ display: 'flex', gap: 8, marginTop: 6, flexShrink: 0 }}>
                {SHOP_PHONE && (
                  <a href={`tel:${SHOP_PHONE}`} style={{ flex: 1, background: '#fff', borderRadius: 10, padding: '9px 4px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, textDecoration: 'none', color: '#0f172a', fontWeight: 700, fontSize: 10.5 }}>
                    <Headphones size={17} /> SUPPORT
                  </a>
                )}
                {SHOP_WHATSAPP && (
                  <a href={waLink(`Hi, I'm interested in ${item.itemName} (SKU ${item.skuNo})`)} target="_blank" rel="noreferrer"
                    style={{ flex: 1, background: '#fff', borderRadius: 10, padding: '9px 4px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, textDecoration: 'none', color: '#0f172a', fontWeight: 700, fontSize: 10.5 }}>
                    <WhatsAppIcon size={17} /> WHATSAPP
                  </a>
                )}
                {SHOP_WHATSAPP && (
                  <a href={waLink(`I want to buy ${item.itemName} (SKU ${item.skuNo}) — ₹${fmt(price)}`)} target="_blank" rel="noreferrer"
                    style={{ flex: 2, background: GOLD, borderRadius: 10, padding: '9px 4px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, textDecoration: 'none', color: NAVY, fontWeight: 800, fontSize: 13 }}>
                    <ShoppingBag size={17} /> BUY NOW
                  </a>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
